import type ExcelJS from "exceljs";

/**
 * Shared BOQ worksheet styling/layout helpers.
 *
 * Extracted from `groundworksExcelExport.ts` (the reference BOQ export layout)
 * so that `ReviewCalculate.tsx`'s Roads & Sewers export can produce an
 * IDENTICAL layout instead of a hand-maintained duplicate that drifts over
 * time. Do not fork these functions per-export again — any layout change
 * belongs here so every BOQ export stays visually identical.
 */

/**
 * Auto-fit worksheet column widths based on actual cell content.
 * Iterates every non-empty cell per column and measures text length.
 * columnMaxWidths: per 1-based column-index cap (overrides defaultMax).
 */
export function autoFitColumns(
  worksheet: ExcelJS.Worksheet,
  options: {
    defaultMin?: number;
    defaultMax?: number;
    padding?: number;
    columnMaxWidths?: Record<number, number>;
    columnMinWidths?: Record<number, number>;
    /** If set, proportionally scale all columns so their total width reaches at least this value.
     *  Use ~100 for A4 portrait and ~140 for A4 landscape to fill the print area. */
    minTotalWidth?: number;
  } = {},
): void {
  const { defaultMin = 8, defaultMax = 20, padding = 2, columnMaxWidths = {}, columnMinWidths = {}, minTotalWidth } = options;

  // Measure max content length per column by scanning all rows.
  // worksheet.columns.forEach only covers explicitly-declared columns; eachRow covers everything.
  const colMaxLen: Record<number, number> = {};

  worksheet.eachRow((row) => {
    row.eachCell({ includeEmpty: false }, (cell, colNum) => {
      try {
        if (cell.isMerged) return;
        const v = cell.value;
        if (v === null || v === undefined) return;
        let text: string;
        if (typeof v === 'string') {
          text = v;
        } else if (typeof v === 'number') {
          text = String(v);
        } else if (v instanceof Date) {
          text = v.toLocaleDateString('en-GB');
        } else {
          const rt = (v as { richText?: Array<{ text?: string }> }).richText;
          text = Array.isArray(rt) ? rt.map((r) => r.text ?? '').join('') : String(v);
        }
        const longest = text.split('\n').reduce((m, l) => Math.max(m, l.length), 0);
        colMaxLen[colNum] = Math.max(colMaxLen[colNum] ?? 0, longest);
      } catch {
        // ignore individual cell errors
      }
    });
  });

  // Apply content-driven widths with per-column floor and cap
  const colNums = Object.keys(colMaxLen).map(Number);
  colNums.forEach((colNum) => {
    const measured = colMaxLen[colNum];
    const capWidth = columnMaxWidths[colNum] ?? defaultMax;
    const floorWidth = columnMinWidths[colNum] ?? defaultMin;
    worksheet.getColumn(colNum).width = Math.min(Math.max(measured + padding, floorWidth), capWidth);
  });

  // Stretch all columns proportionally so total width fills the print area
  if (minTotalWidth && colNums.length > 0) {
    const currentTotal = colNums.reduce((sum, n) => sum + (worksheet.getColumn(n).width ?? defaultMin), 0);
    if (currentTotal < minTotalWidth) {
      const scale = minTotalWidth / currentTotal;
      colNums.forEach((colNum) => {
        const current = worksheet.getColumn(colNum).width ?? defaultMin;
        // Apply scale but still respect the per-column cap
        const capWidth = columnMaxWidths[colNum] ?? defaultMax;
        worksheet.getColumn(colNum).width = Math.min(current * scale, capWidth * scale);
      });
    }
  }
}

/**
 * Writes the standard BOQ sheet header block: branded logo banner (row 1),
 * spacer, sheet title, project details line, "Generated" date line, spacer.
 * Returns the row number the column headers should start on.
 */
export function createWorksheetHeader(
  worksheet: any,
  logoImageId: any,
  sheetTitle: string,
  projectName: string,
  clientName: string,
  region: string,
  primaryColor: string,
  numColumns: number = 6,
  creationDate?: Date,
  logoMergeColumns: number = 6,
): number {
  // Row 1: Logo with merged cells A1:F1 and dark branded background
  if (typeof logoImageId === "number") {
    try {
      worksheet.addImage(logoImageId, {
        tl: { col: 0.15, row: 0.1 },
        ext: { width: 260, height: 110 }, // Further reduced size to prevent overlap
      });
      worksheet.getRow(1).height = 128; // Row height for logo
    } catch (error) {
      console.error("Error adding logo:", error);
    }
  }
  // Merge row 1 up to the specified column and apply dark navy background
  try {
    worksheet.mergeCells(1, 1, 1, logoMergeColumns);
    const logoRowCell = worksheet.getCell("A1");
    logoRowCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF0A2642" },
    };
  } catch (error) {
    console.error("Error styling logo row:", error);
  }

  // Row 2: Spacing row to prevent logo overlap
  worksheet.getRow(2).height = 25;

  // Row 3: Sheet title
  worksheet.getCell("A3").value = sheetTitle.toUpperCase();
  worksheet.getCell("A3").font = {
    bold: true,
    size: 14,
    color: { argb: primaryColor },
    name: "Calibri",
  };
  worksheet.getCell("A3").alignment = {
    horizontal: "left",
    vertical: "middle",
    wrapText: false,
  };
  worksheet.getRow(3).height = 22;

  // Row 4: Project details — Client first, then Project, then Region
  worksheet.getCell("A4").value =
    `Client: ${clientName} | Project: ${projectName} | Region: ${region}`;
  worksheet.getCell("A4").font = {
    size: 10,
    color: { argb: "666666" },
    name: "Calibri",
  };
  worksheet.getCell("A4").alignment = {
    horizontal: "left",
    vertical: "middle",
    wrapText: false,
  };
  worksheet.getRow(4).height = 18;

  // Row 5: Creation date from tender dashboard
  const displayDate = creationDate
    ? creationDate.toLocaleDateString("en-GB")
    : new Date().toLocaleDateString("en-GB");
  worksheet.getCell("A5").value = `Generated: ${displayDate}`;
  worksheet.getCell("A5").font = {
    size: 10,
    color: { argb: "666666" },
    name: "Calibri",
  };
  worksheet.getCell("A5").alignment = {
    horizontal: "left",
    vertical: "middle",
    wrapText: false,
  };
  worksheet.getRow(5).height = 18;

  // Row 6: Empty spacing row
  worksheet.getRow(6).height = 10;

  // Merge cells for header rows (rows 2-6) to span all columns - merge AFTER setting all properties
  try {
    worksheet.mergeCells(2, 1, 2, numColumns); // Row 2: Spacing
    worksheet.mergeCells(3, 1, 3, numColumns); // Row 3: Sheet title
    worksheet.mergeCells(4, 1, 4, numColumns); // Row 4: Project details
    worksheet.mergeCells(5, 1, 5, numColumns); // Row 5: Date
    worksheet.mergeCells(6, 1, 6, numColumns); // Row 6: Empty row
  } catch (error) {
    console.error("Error merging header cells:", error);
  }

  // Column headers will be on row 7
  return 7;
}

/** Writes and styles the column-header row (bold white text on primaryColor fill). */
export function styleColumnHeaders(
  worksheet: any,
  headers: string[],
  headerRowNum: number,
  primaryColor: string,
) {
  headers.forEach((header, index) => {
    const cell = worksheet.getCell(headerRowNum, index + 1);
    cell.value = header;
    cell.font = {
      bold: true,
      color: { argb: "FFFFFF" },
      size: 12,
      name: "Calibri",
    };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: primaryColor },
    };
    cell.alignment = {
      horizontal: "center",
      vertical: "middle",
    };
    cell.border = {
      top: { style: "thin", color: { argb: primaryColor } },
      left: { style: "thin", color: { argb: primaryColor } },
      bottom: { style: "thin", color: { argb: primaryColor } },
      right: { style: "thin", color: { argb: primaryColor } },
    };
  });

  worksheet.getRow(headerRowNum).height = 25;
}

/** Styles a normal (non-header, non-total) data row, alternating row fill on even rows. */
export function styleDataRow(worksheet: any, rowNum: number, colCount: number) {
  for (let col = 1; col <= colCount; col++) {
    const cell = worksheet.getCell(rowNum, col);
    cell.border = {
      top: { style: "thin", color: { argb: "E5E5E5" } },
      left: { style: "thin", color: { argb: "E5E5E5" } },
      bottom: { style: "thin", color: { argb: "E5E5E5" } },
      right: { style: "thin", color: { argb: "E5E5E5" } },
    };
    cell.font = {
      name: "Calibri",
      size: 11,
    };
    cell.alignment = {
      vertical: "middle",
      horizontal: col === 1 || col === 2 ? "left" : "right",
    };

    if (rowNum % 2 === 0) {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "F8F9FA" },
      };
    }
  }

  worksheet.getRow(rowNum).height = 26;
}

/** Styles a main (depth 0) or sub (depth 1+) category header row. */
export function styleCategoryHeaderRow(
  worksheet: any,
  rowNum: number,
  colCount: number,
  isMainCategory = true,
) {
  for (let col = 1; col <= colCount; col++) {
    const cell = worksheet.getCell(rowNum, col);
    cell.font = {
      bold: true,
      underline: true,
      name: "Calibri",
      size: isMainCategory ? 12 : 11,
      color: { argb: isMainCategory ? "003366" : "0066CC" },
    };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: isMainCategory ? "E6F3FF" : "F0F8FF" },
    };
    cell.border = {
      top: { style: "thin", color: { argb: "CCCCCC" } },
      left: { style: "thin", color: { argb: "CCCCCC" } },
      bottom: { style: "thin", color: { argb: "CCCCCC" } },
      right: { style: "thin", color: { argb: "CCCCCC" } },
    };
    cell.alignment = {
      vertical: "middle",
      horizontal: "left",
    };
  }

  worksheet.getRow(rowNum).height = 24;
}

/** Styles a section-total (blue) or grand-total (gold) row. */
export function styleTotalRow(
  worksheet: any,
  rowNum: number,
  colCount: number,
  isGrandTotal = false,
) {
  for (let col = 1; col <= colCount; col++) {
    const cell = worksheet.getCell(rowNum, col);
    cell.font = {
      bold: true,
      name: "Calibri",
      size: isGrandTotal ? 13 : 12,
      color: { argb: isGrandTotal ? "003366" : "000000" },
    };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: isGrandTotal ? "FFD700" : "E6F3FF" },
    };
    cell.border = {
      top: { style: "medium", color: { argb: "003366" } },
      left: { style: "thin", color: { argb: "003366" } },
      bottom: { style: "medium", color: { argb: "003366" } },
      right: { style: "thin", color: { argb: "003366" } },
    };
    cell.alignment = {
      vertical: "middle",
      horizontal: col === 1 ? "left" : "right",
    };
  }

  worksheet.getRow(rowNum).height = isGrandTotal ? 28 : 24;
}

/** Standard A4-portrait, fit-to-width print setup every linear BOQ sheet uses. */
export const BOQ_PAGE_SETUP = {
  paperSize: 9,
  orientation: "portrait" as const,
  fitToPage: true,
  fitToWidth: 1,
  fitToHeight: 0,
  margins: { left: 0.5, right: 0.5, top: 0.75, bottom: 0.75, header: 0.3, footer: 0.3 },
  horizontalCentered: true,
};

/** Standard A3-landscape print setup every wide grid/matrix sheet (Plot Matrix, Run Matrix) uses. */
export const MATRIX_PAGE_SETUP = {
  paperSize: 8,
  orientation: "landscape" as const,
  fitToPage: true,
  fitToWidth: 1,
  fitToHeight: 0,
  scale: 100,
  margins: { left: 0.2, right: 0.2, top: 0.3, bottom: 0.3, header: 0.15, footer: 0.15 },
  horizontalCentered: true,
};
